import React, { useState } from 'react'
import { Alert, Button, Card, CardBody, CardFooter, Col, Input, Row, Table } from 'reactstrap'
import FalconCardHeader from '../common/FalconCardHeader'
import FullPagination from '../common/pagination/FullPagination'
import PropTypes from 'prop-types'
import useSWRFetchPagination from '../../helpers/hooks/useSWRFetchPagination'
import { fetchCondominiumBanks, remove } from '../../helpers/api/banks'
import { isIterableArray } from '../../helpers/utils'
import BankListItem from '../common/BankListItem'
import { SUCCESS_RESPONSE } from '../../helpers/codeResponses/codes'
import { Toast } from './../UI/toast/Toast'
import withRedirect from '../../helpers/hoc/withRedirect'
import handleErrorMessage from '../../helpers/errors/handleErrorMessage'
import ButtonIcon from '../common/ButtonIcon'
import { faInfoCircle, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons'
import { useSelector } from 'react-redux'
import { Message, Or, PreloadState } from '../UI/preloading/PreloadState'
import classNames from 'classnames'

const CondominiumBankList = ({ setRedirectUrl, setRedirect, title, condominiumId, showPagination }) => {

    const { role } = useSelector(state => state.auth.user);
    const [textSearch, setTextSearch] = useState('');
    const [params, setParams] = useState({
        condominiumId,
        search: null
    });
    const {
        data,
        error,
        isLoading,
        mutate,
        meta,
        handler: { nextPage, previousPage, perPage, goToPage }
    } = useSWRFetchPagination(fetchCondominiumBanks, params);

    const handleDelete = async ({ toDeleteId }) => {
        try {
            const res = await remove(toDeleteId);
            if (res.code === SUCCESS_RESPONSE.OK) {
                mutate()
                Toast.fire({
                    type: 'success',
                    title: `Se ha eliminado el banco satisfactoriamente.`,
                });
            }
        } catch (error) {
            handleErrorMessage(error);
        }
    }

    const handleEdit = ({ toEditId }) => {
        setRedirectUrl(`/dashboard/banks/edit/${toEditId}`);
        setRedirect(true);
    }

    const renderBankItems = () => {
        return data.map((bank) => {
            const condominiumBankId = bank?.condominium_banks_id || bank?.condominiums?.[0]?.condominium_banks_id;
            const bankId = bank?.bank_id;
            const idToUse = role === 'manager' ? condominiumBankId : bankId;

            return <BankListItem
                key={idToUse || Math.random()}
                title={bank?.name}
                toDeleteId={idToUse}
                toEditId={bankId}
                account_number={bank?.account_number}
                owner_name={bank?.owner_name}
                currency={bank?.currency}
                owner_dni={bank?.owner_dni}
                email={bank?.email}
                onDeleteAction={handleDelete}
                onEditAction={handleEdit}
                createdAt={bank?.created_at}
                alternativeName="banco"
                options={role === 'manager'}
                mutate={mutate}
            />
        });
    }

    return <>
        <Card>
            <FalconCardHeader title={title}>
                <Row>
                    <Col className="d-flex">
                        <Input
                            type='text'
                            value={textSearch}

                            onKeyDown={(e) => {
                                if (e.key !== 'Enter') return;
                                const value = e.currentTarget.value.trim();
                                setTextSearch(value);
                                setParams((rest) => ({ ...rest, search: value || null }));
                            }}
                            onChange={(event) => {
                                const value = event.target.value;
                                setTextSearch(value);
                                if (value.trim() === '') setParams((rest) => ({ ...rest, search: null }));
                            }} placeholder="Nombre, cuenta, titular, cédula, correo o moneda..." className="d-inline-block search-input mr-2" />
                        <ButtonIcon color='falcon-default' className='text-nowrap' icon={faSearch} onClick={() => {
                            const value = textSearch.trim();
                            setTextSearch(value);
                            setParams((rest) => ({ ...rest, search: value || null }));
                        }} >Buscar</ButtonIcon>
                    </Col>
                    {role === 'manager' &&
                        <Col xs={12} md={'auto'} className='mt-3 mt-md-0 text-center text-md-right'>
                            <ButtonIcon icon={faPlus} color='falcon-default' onClick={() => {
                                setRedirect(true);
                                setRedirectUrl(`/dashboard/banks/create/${condominiumId}`);
                            }}>
                                Añadir banco
                            </ButtonIcon>
                        </Col>}
                </Row>
            </FalconCardHeader>
            <CardBody className={classNames('fs--1', { 'p-0': isIterableArray(data) })}>
                <PreloadState cond={isIterableArray(data)} loading={isLoading}>
                    <Or value={true}>
                        <Table responsive className='mb-0'>
                            <thead>
                                <tr>
                                    <th className='align-middle'>DNI</th>
                                    <th className='align-middle'>Banco</th>
                                    <th className='align-middle'>Titular</th>
                                    <th className='align-middle'>Moneda</th>
                                    <th className='align-middle'>Número de cuenta</th>
                                    <th className='align-middle'>Correo</th>
                                    <th className='text-center align-middle'>Fecha de creación</th>
                                    {role === 'manager' && <th className='text-center align-middle'>Opciones</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {renderBankItems()}
                            </tbody>
                        </Table>
                    </Or>
                    <Or value={false} cond={!!error}>
                        <Or value={false} cond={role}>
                            <Or value={'manager'} cond={!!textSearch[0]}>
                                <Message value={true} color={'info'} className='p-3 mr-3' align='left' icon={faInfoCircle}>
                                    <>
                                        No hemos encontrado un banco que coincida con <b>"{textSearch}"</b>,
                                        puedes crearlo presionando el botón <b>"Añadir banco"</b>.
                                    </>
                                </Message>
                                <Message value={false} color={'info'} className='p-3 mr-3' align='left' icon={faInfoCircle}>
                                    No hay bancos registrados,
                                    puedes crear uno presionando el botón "Añadir banco".
                                </Message>
                            </Or>
                            <Or value={'property'} cond={!!textSearch[0]}>
                                <Message value={true} color={'info'} className='p-3 mr-3' align='left' icon={faInfoCircle}>
                                    <>No hemos encontrado un banco que coincida con <b>"{textSearch}"</b>.</>
                                </Message>
                                <Message value={false} color={'info'} className='p-3 mr-3' align='left' icon={faInfoCircle}>
                                    Aún no hay bancos en este condominio.
                                </Message>
                            </Or>
                        </Or>
                        <Or value={true}>
                            <Alert color="danger" className="mb-0 mr-3">
                                Esta página no está disponible
                                <Button data-testid="Button" color="link" size="sm" className="ml-1 pl-0" onClick={() => { setRedirectUrl(`/dashboard/banks`); setRedirect(true) }}>
                                    Volver
                                </Button>
                            </Alert>
                        </Or>
                    </Or>
                </PreloadState>
            </CardBody>
            {isIterableArray(data) &&
                <CardFooter className='border-top mt-0'>
                    <FullPagination
                        nextPage={nextPage}
                        prevPage={previousPage}
                        goToPage={goToPage}
                        meta={meta}
                        className={'shadow-none'}
                        posText={"bancos"}
                        perPage={perPage} />
                </CardFooter>
            }
        </Card>
    </>
}

CondominiumBankList.propTypes = {
    showPagination: PropTypes.bool
};
CondominiumBankList.defaultProps = {
    title: 'Listado de bancos del condominio',
    description: ''
};

export default withRedirect(CondominiumBankList);